import { useState } from "react";
import { http } from "@/lib/api";
import { Upload, Check, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useCountry } from "@/lib/country";

export default function Enquiry() {
  const { country } = useCountry();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    full_name: "", country: country?.name || "", whatsapp: "", email: "",
    treatment: "", timeline: "", preferred_city: "", hospital_preference: "",
    condition: "", previous_diagnosis: "",
    companion: false, accommodation: false, airport_pickup: false, visa_assistance: false, language_assistance: false,
    document_ids: []
  });
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const upload = async (file) => {
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await http.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setUploadedFiles(u => [...u, data]);
      setForm(f => ({ ...f, document_ids: [...f.document_ids, data.id] }));
    } catch (e) { alert("Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await http.post("/leads", form);
      setResult(data);
    } catch (e) { alert("Submission failed. Please try again."); }
    finally { setSubmitting(false); }
  };

  if (result) return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="enquiry-success">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto" style={{background:"linear-gradient(135deg,#B47A9A,#5E3A5C)"}}><Check className="w-7 h-7 text-white"/></div>
      <h1 className="font-serif text-4xl md:text-5xl text-[#F3E9EC] mt-6">Thank you — your enquiry is with our care team.</h1>
      <p className="mt-4 text-[#F3E9EC]/70">Your enquiry ID:</p>
      <div className="mt-2 font-mono text-2xl text-[#B47A9A]">{result.enquiry_id}</div>
      <p className="mt-6 text-sm text-[#F3E9EC]/60">A MediPats care coordinator will reach out within 24 hours on WhatsApp or email.</p>
      <div className="mt-8 flex gap-4 justify-center"><Link to="/" className="btn-outline-mauve px-6 py-3">Return home</Link></div>
    </div>
  );

  const stepper = [
    "Patient", "Treatment", "Medical", "Travel"
  ];

  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <div className="overline mb-3">Personalised Enquiry</div>
      <h1 className="font-serif text-4xl md:text-5xl text-[#F3E9EC]">Get Your Personalised Cost of Treatment</h1>
      <p className="mt-3 text-[#F3E9EC]/70">Takes about 3 minutes. All information is stored securely.</p>

      <div className="mt-10 flex gap-2 mb-8">
        {stepper.map((s, i) => (
          <div key={s} className={`flex-1 h-1 rounded-full ${i+1 <= step ? "bg-[#B47A9A]" : "bg-[#5E3A5C]/40"}`}/>
        ))}
      </div>

      <div className="mp-card p-8">
        {step === 1 && (
          <div className="space-y-4" data-testid="enquiry-step-1">
            <h3 className="font-serif text-2xl text-[#F3E9EC] mb-2">About you</h3>
            <input className="mp-input" placeholder="Full name" value={form.full_name} onChange={e=>set("full_name", e.target.value)} data-testid="enquiry-fullname"/>
            <input className="mp-input" placeholder="Country of residence" value={form.country} onChange={e=>set("country", e.target.value)} data-testid="enquiry-country"/>
            <input className="mp-input" placeholder="WhatsApp number (with country code)" value={form.whatsapp} onChange={e=>set("whatsapp", e.target.value)} data-testid="enquiry-whatsapp"/>
            <input className="mp-input" placeholder="Email" value={form.email} onChange={e=>set("email", e.target.value)} data-testid="enquiry-email"/>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4" data-testid="enquiry-step-2">
            <h3 className="font-serif text-2xl text-[#F3E9EC] mb-2">Treatment</h3>
            <input className="mp-input" placeholder="Treatment / surgery" value={form.treatment} onChange={e=>set("treatment", e.target.value)}/>
            <select className="mp-select" value={form.timeline} onChange={e=>set("timeline", e.target.value)}>
              <option value="">Timeline</option>
              <option>Within 2 weeks</option><option>1 month</option><option>2-3 months</option><option>Exploring</option>
            </select>
            <input className="mp-input" placeholder="Preferred city (optional)" value={form.preferred_city} onChange={e=>set("preferred_city", e.target.value)}/>
            <input className="mp-input" placeholder="Hospital preference (optional)" value={form.hospital_preference} onChange={e=>set("hospital_preference", e.target.value)}/>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-4" data-testid="enquiry-step-3">
            <h3 className="font-serif text-2xl text-[#F3E9EC] mb-2">Medical</h3>
            <textarea className="mp-textarea min-h-[100px]" placeholder="Brief condition description" value={form.condition} onChange={e=>set("condition", e.target.value)}/>
            <input className="mp-input" placeholder="Previous diagnosis (optional)" value={form.previous_diagnosis} onChange={e=>set("previous_diagnosis", e.target.value)}/>
            <div>
              <label className="cursor-pointer border-2 border-dashed border-[#5E3A5C]/50 rounded-xl p-8 flex flex-col items-center justify-center gap-2 hover:border-[#B47A9A]/60 transition-colors block">
                <Upload className="w-6 h-6 text-[#B47A9A]"/>
                <span className="text-sm text-[#F3E9EC]/70">Upload medical reports (PDF, images)</span>
                <input type="file" hidden onChange={e=>e.target.files[0] && upload(e.target.files[0])} data-testid="enquiry-upload"/>
              </label>
              {uploading && <p className="text-xs text-[#B47A9A] mt-2">Uploading…</p>}
              {uploadedFiles.length > 0 && (
                <ul className="mt-3 space-y-1">{uploadedFiles.map(f => <li key={f.id} className="text-xs text-[#F3E9EC]/70 flex items-center gap-2"><Check className="w-3 h-3 text-[#B47A9A]"/>{f.filename}</li>)}</ul>
              )}
            </div>
          </div>
        )}
        {step === 4 && (
          <div className="space-y-3" data-testid="enquiry-step-4">
            <h3 className="font-serif text-2xl text-[#F3E9EC] mb-2">Travel needs</h3>
            {[
              ["companion","Companion required"],
              ["accommodation","Accommodation assistance"],
              ["airport_pickup","Airport pickup"],
              ["visa_assistance","Visa assistance"],
              ["language_assistance","Language / translation help"],
            ].map(([k, l]) => (
              <label key={k} className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-[#5E3A5C]/20">
                <input type="checkbox" checked={form[k]} onChange={e=>set(k, e.target.checked)} className="w-4 h-4 accent-[#B47A9A]"/>
                <span className="text-[#F3E9EC]/85 text-sm">{l}</span>
              </label>
            ))}
          </div>
        )}
        <div className="mt-8 flex justify-between">
          <button onClick={()=>setStep(s=>Math.max(1,s-1))} disabled={step===1} className="btn-outline-mauve px-5 py-2.5 disabled:opacity-40" data-testid="enquiry-back">Back</button>
          {step < 4
            ? <button onClick={()=>setStep(s=>s+1)} className="btn-primary px-6 py-2.5" data-testid="enquiry-next">Continue</button>
            : <button onClick={submit} disabled={submitting} className="btn-primary px-6 py-2.5" data-testid="enquiry-form-submit-button">{submitting ? "Submitting…" : "Submit enquiry"}</button>}
        </div>
      </div>
    </div>
  );
}
